import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { NumberPlate, AUSTRALIAN_STATES } from "@/components/NumberPlate";
import api, { formatApiError } from "@/lib/api";
import { UserCheck, X, Plus } from "lucide-react";

const RELATIONSHIPS = [
  { v: "family", l: "Family" },
  { v: "friend", l: "Friend" },
  { v: "colleague", l: "Colleague" },
  { v: "ex_partner", l: "Ex-partner" },
  { v: "other", l: "Other" },
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const initialState = (searchParams.get("state") || "").toUpperCase();
  const [step, setStep] = useState(1); // 1: about you, 2: your vouchers
  const [form, setForm] = useState({
    email: "", password: "", name: "", age: 30, gender: "woman", seeking: "men", city: "",
    state: AUSTRALIAN_STATES.includes(initialState) ? initialState : "",
  });
  const [vouchers, setVouchers] = useState([
    { name: "", email: "", relationship: "friend" },
    { name: "", email: "", relationship: "family" },
    { name: "", email: "", relationship: "friend" },
  ]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setV = (i, k, v) => setVouchers((cur) => cur.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const addV = () => vouchers.length < 6 && setVouchers([...vouchers, { name: "", email: "", relationship: "friend" }]);
  const rmV = (i) => setVouchers((cur) => cur.filter((_, j) => j !== i));

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const cityDisplay = form.state && form.city ? `${form.city}, ${form.state}` : (form.city || form.state || "");
      const payload = {
        ...form,
        city: cityDisplay,
        age: Number(form.age),
        vouch_invites: vouchers.filter((v) => v.name.trim()).map((v) => ({ ...v })),
      };
      await register(payload);
      nav("/vouches");
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const canNext1 = form.name && form.email && form.password.length >= 6 && form.age >= 25;
  const filled = vouchers.filter((v) => v.name.trim()).length;

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-10 paper">
      <div className="w-full max-w-2xl">
        <Link to="/" className="inline-block mb-8" data-testid="register-logo"><Logo /></Link>
        <div className="card-paper p-10">
          <div className="flex items-center justify-between mb-6">
            <div className="eyebrow">Application · Step {step} of 2</div>
            <div className="flex gap-1">
              <div className={`h-1 w-10 rounded-full ${step >= 1 ? "bg-ivy" : "bg-ink/10"}`} />
              <div className={`h-1 w-10 rounded-full ${step >= 2 ? "bg-ivy" : "bg-ink/10"}`} />
            </div>
          </div>

          {step === 1 && (
            <>
              <h1 className="font-display text-4xl text-ink" style={{ fontWeight: 400 }}>Begin your <em className="italic text-ivy">application.</em></h1>
              <p className="text-sm text-ink/60 mt-2">Two minutes. Then three vouchers.</p>
              <div className="mt-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-ink/70">Your name</label>
                    <input data-testid="reg-name" required value={form.name} onChange={set("name")}
                      className="mt-1 w-full px-4 py-3 rounded-xl bg-white border border-ink/10" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-ink/70">Age</label>
                    <input data-testid="reg-age" type="number" min="25" max="99" required value={form.age} onChange={set("age")}
                      className="mt-1 w-full px-4 py-3 rounded-xl bg-white border border-ink/10" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-ink/70">I am a…</label>
                    <select data-testid="reg-gender" value={form.gender} onChange={set("gender")}
                      className="mt-1 w-full px-4 py-3 rounded-xl bg-white border border-ink/10">
                      <option value="woman">Woman</option><option value="man">Man</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-ink/70">Seeking</label>
                    <select data-testid="reg-seeking" value={form.seeking} onChange={set("seeking")}
                      className="mt-1 w-full px-4 py-3 rounded-xl bg-white border border-ink/10">
                      <option value="men">Men</option><option value="women">Women</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-ink/70">Your state</label>
                  {form.state ? (
                    <div className="mt-2 flex items-center gap-4" data-testid="reg-state-picked">
                      <NumberPlate state={form.state} plate={`LTL·${form.state}`} size="sm" asButton={false} />
                      <button
                        type="button"
                        data-testid="reg-state-change"
                        onClick={() => setForm((f) => ({ ...f, state: "" }))}
                        className="text-xs text-ivy hover:underline"
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    <div className="mt-2 grid grid-cols-4 gap-2" data-testid="reg-state-picker">
                      {AUSTRALIAN_STATES.map((s) => (
                        <button
                          key={s}
                          type="button"
                          data-testid={`reg-state-${s}`}
                          onClick={() => setForm((f) => ({ ...f, state: s }))}
                          className="px-3 py-2 rounded-lg bg-white border border-ink/10 text-sm font-mono hover:bg-ivy hover:text-parchment transition-all duration-500"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-ink/70">City / suburb</label>
                  <input data-testid="reg-city" value={form.city} onChange={set("city")}
                    className="mt-1 w-full px-4 py-3 rounded-xl bg-white border border-ink/10" placeholder="Ballarat" />
                </div>
                <div>
                  <label className="text-xs font-medium text-ink/70">Email</label>
                  <input data-testid="reg-email" type="email" required value={form.email} onChange={set("email")}
                    className="mt-1 w-full px-4 py-3 rounded-xl bg-white border border-ink/10" placeholder="you@licencetolove.com" />
                </div>
                <div>
                  <label className="text-xs font-medium text-ink/70">Password (min 6)</label>
                  <input data-testid="reg-password" type="password" required minLength={6} value={form.password} onChange={set("password")}
                    className="mt-1 w-full px-4 py-3 rounded-xl bg-white border border-ink/10" />
                </div>
              </div>
              <div className="flex justify-between items-center mt-8">
                <Link to="/login" data-testid="register-goto-login" className="text-sm text-ink/60 hover:underline">I've been invited</Link>
                <button data-testid="reg-next" onClick={() => canNext1 && setStep(2)} disabled={!canNext1}
                  className="btn-pill bg-ivy text-parchment hover:bg-ivy-600 disabled:opacity-40">
                  Next · Your vouchers
                </button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h1 className="font-display text-4xl text-ink" style={{ fontWeight: 400 }}>Who <em className="italic text-ivy">vouches</em> for you?</h1>
              <p className="text-sm text-ink/60 mt-2">Add three people who know you well. We'll create a private link for each — you can send it however you like (SMS, WhatsApp, email). One confirmed vouch and you're in.</p>

              <div className="mt-6 space-y-3">
                {vouchers.map((v, i) => (
                  <div key={i} className="p-4 rounded-xl border border-ink/10 bg-white">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-xs font-semibold text-ivy">
                        <UserCheck size={14} /> Voucher {i + 1}
                      </div>
                      {vouchers.length > 1 && (
                        <button data-testid={`v-rm-${i}`} onClick={() => rmV(i)} className="text-ink/40 hover:text-ochre-300"><X size={16} /></button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1.5fr_1fr] gap-2">
                      <input data-testid={`v-name-${i}`} value={v.name} onChange={(e) => setV(i, "name", e.target.value)}
                        placeholder="Their name" className="px-3 py-2 rounded-lg bg-parchment border border-ink/10 text-sm" />
                      <input data-testid={`v-email-${i}`} value={v.email} onChange={(e) => setV(i, "email", e.target.value)}
                        placeholder="Email (optional)" className="px-3 py-2 rounded-lg bg-parchment border border-ink/10 text-sm" />
                      <select data-testid={`v-rel-${i}`} value={v.relationship} onChange={(e) => setV(i, "relationship", e.target.value)}
                        className="px-3 py-2 rounded-lg bg-parchment border border-ink/10 text-sm">
                        {RELATIONSHIPS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
                {vouchers.length < 6 && (
                  <button data-testid="v-add" onClick={addV} className="w-full py-3 rounded-xl border border-dashed border-ink/20 text-sm text-ink/60 hover:bg-white/50 flex items-center justify-center gap-2">
                    <Plus size={14} /> Add another voucher
                  </button>
                )}
              </div>

              <div className="mt-4 p-4 rounded-xl bg-amber/10 border border-amber-200 text-sm text-ink/80">
                <strong className="text-ochre-300">The rule:</strong> one confirmed vouch lets you in (provisional license). You have 14 days to reach three, after which your license is fully issued.
              </div>

              {err && <div className="text-sm text-ochre-300 mt-4" data-testid="register-error">{err}</div>}

              <div className="flex justify-between items-center mt-8">
                <button data-testid="reg-back" onClick={() => setStep(1)} className="text-sm text-ink/60 hover:underline">← Back</button>
                <button data-testid="register-submit" onClick={submit} disabled={busy || filled < 1}
                  className="btn-pill bg-ivy text-parchment hover:bg-ivy-600 hover:scale-[1.01] disabled:opacity-40">
                  {busy ? "Applying..." : `Submit application (${filled} voucher${filled === 1 ? "" : "s"})`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
